import { _ } from "../../core/polyglot/polyglot.js";
import { homeIcon, plusIcon } from "../../core/elements/icons/index.js";
import { makeMonitorRepository } from "../../core/repositories/repositoryBuilders.js";
import accountingDocument from "../shared/accountingDocument.js";
import EmptyArchiveWidget from "../../core/widgets/emptyArchive/EmptyArchiveWidget.js";
import FileSelectionButtonWidget from "../../core/widgets/FileSelectionButtonWidget.js";
import ItemPicker from "../../core/widgets/ItemPicker/ItemPicker.js";
import popoverActionButtonWidget from "../../core/widgets/popoverActionButtonWidget.js";
import ReportTemplate from "../../models/reports/templates/ReportTemplate.js";
import session from "../../core/session.js";

/**
 * Todo: Add JSDoc comment
 */
function customizedReportArchiveDocument(spec, my) {
	my ||= {};
	spec ||= {};

	const that = accountingDocument(spec, my);

	my.templates = [];

	const antiForgeryToken = session.getAntiForgeryToken();

	my.templateRepository = makeMonitorRepository({
		route: "/Shared/ReportTemplate/Company/#companyId/?id",
		model: ReportTemplate,
		defaultFilters: {
			companyId: session.getCompany().getLegacyId(),
		},
		antiForgeryToken,
	});

	my.companyTypeCustomTemplatesRepository = makeMonitorRepository({
		route: "/Shared/ReportTemplate/CompanyType/#companyTypeId",
		model: ReportTemplate,
		defaultFilters: {
			companyTypeId: session.getCompany().getCompanyTypeId(),
		},
		antiForgeryToken,
	});

	my.reportItemPickerWidget = new ItemPicker({
		itemRenderer: (item) => item.title,
		standalone: true,
	});

	my.popoverButton = popoverActionButtonWidget({
		label: _("Common Archive"),
		popoverHeader: _("Common Archive"),
		popoverContent: my.reportItemPickerWidget,
		popoverAction: () => {
			const selected = my.reportItemPickerWidget.getSelected();
			if (selected) {
				my.copyTemplate(selected.id);
			}
		},
		popoverPrimaryButtonLabel: _("Import Template"),
		popoverCssClass: "popover",
		dataTestName: "common-archive",
	});

	that.documentId = "customized-reports";

	my.createNewTemplateLink = my.linkTo("new-custom-report", { reset: true });

	that.hasPeriodPicker = function () {
		return false;
	};

	that.hasActionsMenu = function () {
		return my.canCreateTemplates();
	};

	that.actions = function () {
		if (!my.canCreateTemplates()) {
			return [];
		}

		return [
			my.newTemplateActionsRenderer(),
			my.importTemplateActionsRenderer(),
		];
	};

	that.title = _("Customized reports");
	that.subtitle = _("Tailor-made reports for your company");

	that.help = function () {
		return [
			{
				title: _("Customized reports"),
				content: _(
					"The customized report archive is a digital archive where all custom reports are stored. New report templates, tailor-made for your company, will be accessed from here.",
				),
			},
			{
				title: _("Create a new template"),
				content: _(
					"As a consultant, please click this button to create a new template.",
				),
				element: "create-new-template-button",
			},
		];
	};

	that.show = async function () {
		that.showUpdating();

		const templates = await my.templateRepository.get();

		my.reportItemPickerWidget.setItems(
			await my.getCompanyTypeCustomTemplates(),
		);

		my.templates = templates
			.filter((template) => !template.isKnown())
			.sort((a, b) => a.getTitle().localeCompare(b.getTitle()));

		if (my.templates.length) {
			that.showContent();
		} else {
			that.showEmpty();
		}
		my.updateActions();
	};

	my.renderEmptyContentOn = function (html) {
		html.render(
			new EmptyArchiveWidget({
				title: _("This archive has no report yet"),
				subtitle: my.canCreateTemplates()
					? _("Come on, let's get started!")
					: _("Ask your consultant to create custom reports for you"),
				actions: (html) => my.renderEmptyArchiveActionsOn(html),
			}),
		);
	};

	my.renderEmptyArchiveActionsOn = function (html) {
		if (!my.canCreateTemplates()) {
			return;
		}

		html.a(
			{
				klass: "btn",
				href: my.createNewTemplateLink,
			},
			plusIcon,
			_("Create your first custom report"),
		);

		html.render(
			FileSelectionButtonWidget.forJSONFile({
				label: [
					(html) => html.i({ klass: "fas fa-cloud-upload-alt" }),
					_("Import a report"),
				],
				cssClass: "btn",
				fileSelectedCallback: (template) => my.saveTemplate(template),
			}),
		);
	};

	my.renderContentOn = function (html) {
		html.render(my.renderBreadcrumbsOn, my.renderTemplatesOn);
	};

	my.renderBreadcrumbsOn = function (html) {
		html.div({ klass: "breadcrumbs" }, homeIcon);
	};

	my.renderTemplatesOn = function (html) {
		html.div(
			{ klass: "report-group" },
			html.div(
				{ klass: "folders" },
				my.templates.map((template) => my.templateRenderer(template)),
				my.renderNewTemplateOn,
			),
		);
	};

	my.templateRenderer = function (template) {
		return function (html) {
			html.div(
				{ klass: "folder", title: template.getTitle() },
				html.div(
					{
						klass: [
							"icon",
							{ "user-hidden": !template.isVisibleToAllUsers() },
						],
					},
					html.img({ src: "/Client/img/fp-icon-folder.png" }),
				),
				html.p(template.getTitle()),
				html.span(template.getDescription()),
			).click(() => {
				my.redirectTo(`customized-reports/${template.getId()}`);
			});
		};
	};

	my.renderNewTemplateOn = function (html) {
		if (!my.canCreateTemplates()) {
			return;
		}

		html.div(
			{ klass: "folder folder-add" },
			html.span({ klass: "glyphicon glyphicon-plus" }),
			html.p(_("New report template")),
		).click(() => {
			my.redirectTo("new-custom-report", { reset: true });
		});
	};

	my.newTemplateActionsRenderer = function () {
		return my.rightMenuSection(_("New template"), (html) => {
			html.a(
				{
					klass: "btn btn-default btn-block btn-info",
					"data-help": "create-new-template-button",
					href: my.createNewTemplateLink,
					"data-test": "create-template",
				},
				_("Create new template"),
			);
		});
	};

	my.importTemplateActionsRenderer = function () {
		return my.rightMenuSection(_("Import templates"), [
			my.renderFileSelectionButtonOn,
			my.renderPopoverButtonOn,
		]);
	};

	my.renderFileSelectionButtonOn = function (html) {
		html.render(
			FileSelectionButtonWidget.forJSONFile({
				label: _("From file"),
				cssClass: "btn btn-default btn-block",
				fileSelectedCallback: (template) => my.saveTemplate(template),
			}),
		);
	};

	my.renderPopoverButtonOn = function (html) {
		html.render(my.popoverButton);
	};

	my.saveTemplate = async function (template) {
		await my.templateRepository.save({
			model: template,
		});

		my.dirty = false;
		my.redirectTo(`report-editor/${template.id}`);
	};

	my.canCreateTemplates = function () {
		return session
			.getCurrentUser()
			.isAdminUser(session.getCompany().getLegacyId());
	};

	my.getCompanyTypeCustomTemplates = async function () {
		const customReports =
			await my.companyTypeCustomTemplatesRepository.get();

		return customReports.map((customReport) => ({
			title: customReport.getTitle(),
			id: customReport.getId(),
			description: customReport.getDescription(),
			selected: false,
			match: (searchString) => customReport.match(searchString),
		}));
	};

	my.copyTemplate = async function (templateId) {
		const newTemplate = await my.templateRepository.save({
			filter: { reportTemplateId: templateId },
		});

		my.dirty = false;
		my.redirectTo(`report-editor/${newTemplate.getId()}`);
	};

	return that;
}

export default customizedReportArchiveDocument;
